import { FC, useCallback } from 'react'
import { IconButton, Modal } from '@cargomart/ui'
import { notify } from 'components/extend/Notification/NotificationEmitter'
import { MODAL_IDS } from 'components/ModalProvider'
import { getEnv } from 'helpers/common'
import IconFileCopy from 'remixicon/icons/Document/file-copy-line.svg?react'
import { OrderItem } from 'schema'
import { MultiAccordion } from 'src/components/MultiAccordion'
import { paymentForm } from 'src/utils/feature'

import { useFindOrderPaymentQuery } from '../../api'
import { formattedSerialId } from '../../helpers'
import { OrderPaymentSection } from '../OrderPaymentSection'
import { HELP_ITEMS } from './constants'
import OrderDetailRoutePoint from './OrderDetailRoutePoint'
import OrderYarcheDetailSummary from './OrderYarcheDetailSummary'
import OrderYarcheAccepted from './OrderYarcheAccepted'
import OrderYarcheDetailMobileHeader from './OrderYarcheDetailMobileHeader'
import OrderYarcheTracking from './OrderYarcheTracking'

interface OrderYarcheDetailMobileProps {
  onClose: () => void
  order: OrderItem
}

const OrderYarcheDetailMobile: FC<OrderYarcheDetailMobileProps> = (props) => {
  const { order, onClose } = props

  const { data: payment, isLoading: isPaymentLoading } = useFindOrderPaymentQuery(order?.id ? order?.id : '', {
    skip: !order?.id
  })

  // Показываем предупреждение о НДС для невыданных заявок (не DELIVERED и не CANCEL)
  const isShowVatWarning = order?.status !== OrderItem.Status.DELIVERED && order?.status !== OrderItem.Status.CANCEL

  const handleCopyOrderTracking = useCallback(() => {
    if (!order?.barcode) {
      return
    }

    const { barcode } = order
    const link = `${getEnv('VITE_REACT_APP_LANDING_URL')}/tracking/${barcode}`

    navigator.clipboard.writeText(link).then(() => {
      notify({ type: 'success', title: 'Ссылка на историю перевозки скопирована' })
    })
  }, [order])

  return (
    <Modal
      isCloseOnEsc
      isCloseOnOutsideClick
      isMobile
      className="!bg-background pb-24"
      id={MODAL_IDS.OrderYarcheDetail}
      onClose={onClose}
    >
      <OrderYarcheDetailMobileHeader order={order} />
      <div className="mb-2 rounded-b-lg bg-surface">
        <div className="self-center px-4 py-2 text-headline-md">{formattedSerialId(order?.serialId)}</div>
        {order?.barcode && (
          <div className="flex items-center justify-between py-1 pl-4 pr-1">
            <div className="text-title-md">Трек&nbsp;&#8470;&nbsp;{order.barcode}</div>
            <IconButton className="ml-auto" onClick={handleCopyOrderTracking} size="lg">
              <IconFileCopy size="nm" />
            </IconButton>
          </div>
        )}
        {order?.barcode && <OrderYarcheAccepted barcode={order.barcode} className="mx-4 mb-4" status={order.status} />}
        {order.status === OrderItem.Status.NEW && (
          <div className="m-4">
            <MultiAccordion items={HELP_ITEMS} searchId="HELP_ITEMS" title="Помощь" />
          </div>
        )}
      </div>

      {order?.barcode && order?.isCargoAccepted && (
        <div className="mb-2 rounded-lg bg-surface px-4 py-2">
          <OrderYarcheTracking barcode={order.barcode} />
        </div>
      )}
      <div className="mb-2 rounded-lg bg-surface px-4 py-2">
        <OrderYarcheDetailSummary order={order} titleClassName="text-title-lg" />
      </div>
      <OrderDetailRoutePoint
        className="mb-2 rounded-lg bg-surface px-4 py-2"
        contact={order.departure.partner.contact}
        mode="departurePoint"
        terminal={order.departure.point.terminal}
      />
      <OrderDetailRoutePoint
        className="mb-2 rounded-lg bg-surface px-4 py-2"
        contact={order.destination.partner.contact}
        mode="destinationPoint"
        terminal={order.destination.point.terminal}
      />
      {paymentForm && (
        <div className="mb-2 rounded-lg bg-surface px-4 py-2">
          <OrderPaymentSection
            isDocumentInvoiceLoading={false}
            isFetching={false}
            isPaymentLoading={isPaymentLoading}
            order={order}
            payment={payment}
          />
        </div>
      )}
    </Modal>
  )
}

export default OrderYarcheDetailMobile
